<!-- eslint-disable max-len -->
<template>
  <div class="experiences-filter-wrapper">
    <simple-list
      ref="list"
      :server-items="availableExperiences"
      query="categories.label"
      :undefined-value="dictionary.allExperiences"
      variant="experience-list"
      class="experience-list"
    >
      <template #filter="{ filters }">
        <div class="experience-list-header">
          <accessible-tabs>
            <div
              role="tablist"
              class="filter-items"
            >
              <button
                id="exp-filter-all"
                class="filter-item tab-group-button"
                :class="[{ active: !currentCategory }]"
                type="button"
                role="tab"
                :aria-selected="!currentCategory"
                @click="filterCategory($event)"
                @keydown.right.prevent="filterCategory($event, categories[0].label)"
                @keydown.left.prevent="
                  filterCategory($event, categories[categories.length - 1].label)
                "
              >
                <span>{{ dictionary.allExperiences }}</span>
              </button>

              <button
                v-for="(category, i) in categories"
                :id="'exp-filter-' + i"
                :key="category.label"
                class="filter-item tab-group-button"
                :class="[
                  {
                    active: currentCategory === category.label,
                    disabled: categoryIsDisabled(category.label),
                  },
                ]"
                type="button"
                role="tab"
                :aria-selected="currentCategory === category.label"
                :tabindex="filters['categories.label'] === category.label && !categoryIsDisabled(category.label) ? '0' : '-1'"
                @click="filterCategory($event, category.label)"
                @keydown.right.prevent="
                  filterCategory(
                    $event,
                    i === categories.length - 1 ? null : categories[i + 1].label
                  )
                "
                @keydown.left.prevent="
                  filterCategory(
                    $event,
                    i === 0 ? null : categories[i - 1].label
                  )
                "
              >
                <span>{{ category.label }}</span>
              </button>
            </div>
          </accessible-tabs>
          <experiences-filter
            v-if="showExperiencesFilter"
            ref="experienceFilter"
            :experiences="experiences"
            :experience-type-filter="filters"
            :options="options"
            :dictionary="dictionary"
            @updateFilteredExperiences="updateExperiencesAvailable"
          >
            <button
              slot-scope="{
                activate,
                isActive,
                resetAllFilters,
                totalFiltersSelected,
              }"
              class="experiences-filter--activator"
              :class="totalFiltersSelected ? 'filter-selected' : ''"
              @click="activate"
            >
              <span
                class="experiences-filter--activator-label"
                :class="totalFiltersSelected ? 'filter-selected' : ''"
              >{{ dictionary.filter }}</span>
              <span
                v-if="totalFiltersSelected > 0"
                class="experiences-filter--activator-total-tag"
              >{{ totalFiltersSelected }}</span>
              <span class="icon-filter-black" />
              <button
                v-if="totalFiltersSelected > 0"
                :tabindex="isActive ? '-1' : '0'"
                class="experiences-filter--activator-reset"
                @click.stop="resetAllFilters"
              >
                {{ dictionary.reset }}
              </button>
            </button>
          </experiences-filter>
        </div>
      </template>
      <template #no-items>
        <div class="error-box no-margin">
          <span>
            {{ dictionary.noResultMessage }}
          </span>
        </div>
      </template>
      <template #rendered-list="{ items }">
        <ul>
          <li
            v-for="item in items"
            :key="item.id"
          >
            <experience-card
              class="room-item"
              :experience="item"
              :filter="currentCategory"
              :dictionary="dictionary"
              :eyebrow-source="options.eyebrowSource"
            />
          </li>
        </ul>
      </template>
      <template #pagination="{ pagination, defaultLimit, allLoaded }">
        <div class="pagination">
          <load-more-pagination
            v-model="pagination"
            variant="experience-list"
            v-bind="{ defaultLimit, allLoaded }"
            :load-more-text="dictionary.loadMore"
          />
        </div>
      </template>
    </simple-list>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import ExperiencesFilter from '@components/01.base/experiences/experiences-filter.vue'
  import ExperienceCard from '@components/01.base/experiences/experience-card.vue'
  import LoadMorePagination from '@components/01.base/02.lists/02.pagination-types/load-more-pagination.vue'
  import sift from 'sift'
  import AccessibleTabs from '../accessible-tabs/accessible-tabs'

  export default {
    name: 'ExperienceList',
    components: {
      LoadMorePagination,
      ExperienceCard,
      ExperiencesFilter,
      AccessibleTabs
    },

    props: {
      experiences: {
        type: Array,
        default: () => ([])
      },
      categories: {
        type: Array,
        default: () => ([])
      },
      options: {
        type: Object,
        required: true,
        default: () => ({})
      },
      dictionary: {
        type: Object,
        required: true,
        default: () => ({})
      }
    },

    data() {
      return {
        availableExperiences: [],
        properties: [],
        seasons: [],
        isOpen: false
      }
    },
    watch: {
      filteredItems(val) {
        this.availableExperiences = val
      },
      availableExperiences: {
        handler(val) {
          this.$refs.list.loadItems({ items: val, total: val.length })
        },
        deep: true,
      },
    },

    mounted() {
      this.availableExperiences = this.experiences
      if (this.$route.query.category) {
        this.updateCategory({ 'categories.label': this.$route.query.category })
      }
    },
    computed: {
      ...mapState('responsive', { wWidth: 'width', wHeight: 'height' }),
      ...mapGetters('i18n', ['tc']),

      currentCategory() {
        return this.$route.query.category
      },

      showExperiencesFilter() {
        return (
          this.experiences && this.experiences.length > 0
        )
      }
    },
    methods: {
      handleCloseModal() {
        document.body.classList.remove('no-scroll')
        this.isOpen = false
      },

      handleOpenModal() {
        document.body.classList.add('no-scroll')
        this.isOpen = true
      },

      updateCategory(filter) {
        this.$refs.list.updateFilter(filter)
      },

      filterCategory(e, category) {
        e.preventDefault()
        const categoryIndex = this.categories.findIndex((item) => item.label === category)
        if (category && this.$route.query.category !== category) {
          this.$router.replace({
            query: {
              ...this.query,
              category,
            },
          })
          this.updateCategory({ 'categories.label': category })
          if (!this.$refs.experienceFilter?.isOpen) {
            document.querySelector(`#exp-filter-${categoryIndex}`).focus()
          }
        } else if (!category) {
          const newQuery = {
            ...this.query,
          }
          delete newQuery.category
          this.$router.replace({ query: newQuery }).catch(() => {})
          this.updateCategory({})
          if (!this.$refs?.experienceFilter?.isOpen) {
            document.querySelector('#exp-filter-all').focus()
          }
        }
      },

      updateExperiencesAvailable(filteredExperiences) {
        this.availableExperiences = filteredExperiences
      },

      categoryIsDisabled(category) {
        return this.$refs?.experienceFilter?.getFilteredExperiences(this.experiences, false).filter(sift({ 'categories.label': category })).length === 0
      },
    }
  }
</script>
